import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { fetchSettings, getCached, SETTINGS_KEY } from '../services/publicContent';
import { getDefaultSettings } from '../services/settingsDefaults';

const SettingsContext = createContext();

/**
 * SettingsProvider — provides clinic settings (address, hours, phone, socials)
 * to the whole app.
 *
 * Reads via the LITE SDK (services/publicContent.js) so the public bundle
 * skips the full real-time SDK (~70 kB gzip); admin edits show on next load.
 * Initial state prefers the react-snap snapshot, else DEFAULT_SETTINGS, so
 * the first client render matches the pre-rendered HTML.
 */
export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(
        () => getCached(SETTINGS_KEY) ?? getDefaultSettings(),
    );
    const [loading, setLoading] = useState(getCached(SETTINGS_KEY) === undefined);

    useEffect(() => {
        let cancelled = false;
        fetchSettings()
            .then((data) => {
                if (!cancelled) setSettings(data);
            })
            .catch((error) => {
                // Defaults are already rendered — log and move on.
                console.warn('Settings fetch failed, using defaults:', error.message);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    const value = useMemo(() => ({ settings, loading }), [settings, loading]);

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};

/** Hook to access clinic settings from any component */
export const useSettings = () => {
    const context = useContext(SettingsContext);
    // Fallback for safety — should never happen if SettingsProvider wraps the app
    if (!context) return { settings: getDefaultSettings(), loading: true };
    return context;
};
